import { gql } from '@apollo/client';

export const GET_SUBSCRIPTION_PACKAGES = gql`
  query GetAvailablePackages {
    getAvailablePackages {
      id
      packageCode
      name
      description
      priceWeekly
      priceMonthly
      priceOneTime
      billingModel
      accessDurationDays
      allowMultipleSubscriptions
      displayOrder
      isActive
      hasTrackRestrictions
      saleEndsAt
      accessEndsOn
      createdAt
      updatedAt
      tagRules {
        id
        tag {
          id
          name
        }
      }
    }
  }
`;

/** One public package by code, for a direct checkout link (/buy/:packageCode). */
export const GET_PACKAGE_OFFER = gql`
  query GetPackageOffer($packageCode: String!) {
    getPackageOffer(packageCode: $packageCode) {
      id
      packageCode
      name
      description
      priceWeekly
      priceMonthly
      priceOneTime
      billingModel
      accessDurationDays
      saleEndsAt
      accessEndsOn
      onSale
    }
  }
`;

export interface PackageOffer {
  id: string;
  packageCode: string;
  name: string;
  description?: string | null;
  priceWeekly: number;
  priceMonthly: number;
  priceOneTime?: number | null;
  billingModel: 'SUBSCRIPTION' | 'ONE_TIME';
  accessDurationDays?: number | null;
  saleEndsAt?: string | null;
  accessEndsOn?: string | null;
  onSale?: boolean | null;
}

/** The package the brand gives every new account at registration, if any. */
/** The free trial signing up today gets, from the brand's trial package; null = no trial. */
export interface TrialOffer {
  days: number;
}

export const GET_TRIAL_OFFER = gql`
  query GetTrialOffer {
    getTrialOffer {
      days
    }
  }
`;

export const GET_PROMO_PACKAGE = gql`
  query GetPromoPackage {
    getPromoPackage {
      id
      packageCode
      name
      description
      priceWeekly
      priceMonthly
      displayOrder
      isActive
      isPrivate
      isDefaultForAllUsers
      allowMultipleSubscriptions
      billingModel
      accessDurationDays
      priceOneTime
      hasTrackRestrictions
      isPromoPackage
      saleEndsAt
      accessEndsOn
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_SUBSCRIPTIONS = gql`
  query GetCurrentUserSubscription {
    getCurrentUserSubscription {
      activeSubscriptions {
        id
        userId
        package {
          id
          packageCode
          name
          description
          priceWeekly
          priceMonthly
          priceOneTime
          billingModel
          accessDurationDays
          allowMultipleSubscriptions
          displayOrder
          isActive
          createdAt
          updatedAt
          tagRules {
            id
            tag {
              id
              name
            }
          }
        }
        billingCycle
        subscriptionOrder
        startDate
        endDate
        isActive
        autoRenew
        createdAt
        updatedAt
        currentPrice
        selectedTracks {
          id
          userSubscriptionId
          trackId
          trackName
          trackState
          trackType
          createdAt
        }
      }
      totalWeeklyCost
      totalMonthlyCost
      currentBillingCycle
      accessibleTracks
    }
  }
`;

export const GET_ALL_USER_SUBSCRIPTIONS = gql`
  query GetAllUserSubscriptions($meetingDate: String) {
    getCurrentUserSubscription(meetingDate: $meetingDate) {
      activeSubscriptions {
        id
        userId
        package {
          id
          packageCode
          name
          description
          priceWeekly
          priceMonthly
          priceOneTime
          billingModel
          accessDurationDays
          allowMultipleSubscriptions
          displayOrder
          isActive
          createdAt
          updatedAt
          tagRules {
            id
            tag {
              id
              name
            }
          }
        }
        billingCycle
        subscriptionOrder
        startDate
        endDate
        isActive
        autoRenew
        createdAt
        updatedAt
        currentPrice
        isDefaultPackage
        selectedTracks {
          id
          userSubscriptionId
          trackId
          trackName
          trackState
          trackType
          createdAt
        }
        appliedDiscount {
          id
          discountCode
          discountName
          discountDescription
          discountAmount
          originalAmount
          finalAmount
          paymentReference
          paymentType
          appliedAt
          stripePromotionCodeId
          stripeCouponId
        }
        cancelAtPeriodEnd
        currentPeriodEnd
        canceledAt
        pendingPackageCode
        pendingBillingCycle
        pendingEffectiveDate
      }
      expiredSubscriptions {
        id
        userId
        package {
          id
          packageCode
          name
          description
          priceWeekly
          priceMonthly
          priceOneTime
          billingModel
          accessDurationDays
          allowMultipleSubscriptions
          displayOrder
          isActive
          createdAt
          updatedAt
          tagRules {
            id
            tag {
              id
              name
            }
          }
        }
        billingCycle
        subscriptionOrder
        startDate
        endDate
        isActive
        autoRenew
        createdAt
        updatedAt
        currentPrice
        selectedTracks {
          id
          userSubscriptionId
          trackId
          trackName
          trackState
          trackType
          createdAt
        }
        appliedDiscount {
          id
          discountCode
          discountName
          discountDescription
          discountAmount
          originalAmount
          finalAmount
          paymentReference
          paymentType
          appliedAt
          stripePromotionCodeId
          stripeCouponId
        }
        cancelAtPeriodEnd
        currentPeriodEnd
        canceledAt
      }
      totalWeeklyCost
      totalMonthlyCost
      currentBillingCycle
      accessibleTracks
    }
  }
`;

export const CALCULATE_PACKAGE_PRICING = gql`
  query CalculateMultiPackagePrice($packageIds: [Int!]!, $billingCycle: BillingCycle!) {
    calculateMultiPackagePrice(packageIds: $packageIds, billingCycle: $billingCycle) {
      packages {
        id
        packageCode
        name
        description
        priceWeekly
        priceMonthly
        priceOneTime
        billingModel
        accessDurationDays
        allowMultipleSubscriptions
        displayOrder
        isActive
        createdAt
        updatedAt
      }
      billingCycle
      totalWeeklyPrice
      totalMonthlyPrice
      monthlySavings
      discountPercentage
      message
    }
  }
`;

export const CREATE_SUBSCRIPTION = gql`
  mutation CreateSubscription($packageIds: [Int!]!, $billingCycle: BillingCycle!) {
    createSubscription(packageIds: $packageIds, billingCycle: $billingCycle)
  }
`;

export const CHANGE_SUBSCRIPTION_PLAN = gql`
  mutation ChangeSubscriptionPlan($newPackageId: Int!, $newBillingCycle: BillingCycle!) {
    changeSubscriptionPlan(newPackageId: $newPackageId, newBillingCycle: $newBillingCycle)
  }
`;

export const CANCEL_PENDING_PLAN_CHANGE = gql`
  mutation CancelPendingPlanChange {
    cancelPendingPlanChange
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($subscriptionId: Int!) {
    cancelSubscription(subscriptionId: $subscriptionId)
  }
`;

export const UPDATE_SUBSCRIPTION = gql`
  mutation UpdateSubscription($subscriptionLevel: String!) {
    updateSubscription(subscriptionLevel: $subscriptionLevel) {
      id
      email
      firstName
      lastName
      subscriptionLevel
      preferences
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_INVOICES = gql`
  query GetUserInvoices {
    getUserInvoices {
      id
      userId
      stripeInvoiceId
      stripeCustomerId
      stripeSubscriptionId
      amountPaid
      amountDue
      amountRemaining
      subtotal
      total
      currency
      status
      paidAt
      billingReason
      createdDate
      invoicePdfUrl
      hostedInvoiceUrl
      invoiceNumber
      failureReason
      createdAt
      updatedAt
      gstAmount
      netAmount
      packageName
      billingCycle
    }
  }
`;